import React, {
    useEffect,
    useRef,
    useContext,
    Fragment,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import Velocity from 'velocity-animate';
import compose from 'lodash/flowRight';

import withAvailableDeliveryDates from 'app/containers/withAvailableDeliveryDates';
import { menuDatesState } from 'app/containers/contexts/menuDates.context';
import { analyticService } from 'global/services';

import { formatPeriodMobile, formatDateToISO } from 'app/utils/date';
import { usePrevious } from 'app/utils/hooks';

import Checkbox, { TYPE_RADIO } from 'app/components/Form/Checkbox2';

import LINKS from 'app/const/links';

import { DIFFS } from 'app/containers/contexts/menuDates.consts';

import './period-list.scss';
import { gql, useMutation, useReactiveVar } from '@apollo/client';
import { onCompletedBillingMutation } from 'app/containers/BasketResolver';
import { isGlobalLoadingVar, regionSubdivisionVar, selectedPeriodVar } from 'app/apollo/reaction';
import { useBillingMutationVars } from 'app/hooks/accessors/useBillingMutationVars';
import { isDateInRange } from 'app/containers/contexts/filters.context/filters.context';
import {
    excludeHiddenPeriods, isDateHidden, isHiddenDatesExcludedOnPage,
} from 'app/const/hiddenPeriods';


const { EVERYDAY_SET } = LINKS;

const ANIMATION_OPTIONS = {
    duration: 400,
    easing: 'ease-in-out',
};

function animateExpand(el) {
    Velocity(
        el,
        'slideDown',
        ANIMATION_OPTIONS,
    );
}

function animateCollapse(el, options = {}) {
    Velocity(
        el,
        'slideUp',
        {
            ...ANIMATION_OPTIONS,
            ...options,
        },
    );
}

const D_PERIOD_SELECT_BILLING_MUTATION = gql`
    mutation D_PERIOD_SELECT_BILLING_MUTATION(
        $promocode: String,
        $period: String!,
        $payment_method: paymentMethod,
        $phone: String,
    ) {
        billing(
            promocode: $promocode,
            period: $period,
            payment_method: $payment_method,
            phone: $phone,
        ) {
            promocode
            payment_method
            isNewbie
            cart {
                period
                isSubscriptionActivated
                id
                type
            totals {
                ...ROOT_CART_TOTAL
            }
            discount_conditions {
                ...ROOT_CART_DISCOUNT_CONDITIONS
                }
            }
        }
    }
`;


const defaultTomorrowDeliveryDate = { dateObj: null };


function PeriodList({
    periods,
    selectedPeriod,
    onSelect,
    isExpanded = false,
    locale,

    tomorrowDeliveryDate = defaultTomorrowDeliveryDate,
    deliveryDatesEveryday,
    deliveryDatesEE,
    deliveryDates,
}) {
    const containerRef = useRef();
    const prevIsExanded = usePrevious(isExpanded);
    const curPeriod = useReactiveVar(selectedPeriodVar);
    const regionSubdivision = useReactiveVar(regionSubdivisionVar);
    const billingVars = useBillingMutationVars();
    const [renderPeriodList, setRenderPeriodList] = useState(periods);

    const {
        onSelectDate,
        state: {
            currentDate,
            dateFilterStatus,
            filterPeriod,
        },
    } = useContext(menuDatesState);

    useEffect(() => {
        const el = containerRef.current;

        if (prevIsExanded === undefined && el) {
            animateCollapse(el, { duration: 0 });
            return;
        }

        const isExpandStateChanged = isExpanded !== prevIsExanded;

        if (!isExpandStateChanged || !el) return;

        if (isExpanded) {
            animateExpand(el);
        } else {
            animateCollapse(el);
        }
    });

    useEffect(
        () => {
            setRenderPeriodList(periods);
        },
        [regionSubdivision, periods],
    );

    const [updateBilling] = useMutation(D_PERIOD_SELECT_BILLING_MUTATION);

    // next onSubmitHandler
    // const switchToNextPeriodOrCity = () => {
    //     isGlobalLoadingVar(true);
    //     updateBilling({
    //         onCompleted: (data) => { onCompletedBillingMutation(data); },
    //         variables: {
    //             ...billingVars,
    //             period: uiSelectedPeriod,
    //         },
    //     }).then(({ data }) => {
    //         const nextPriod = data?.billing?.cart?.period;
    //         selectedPeriodVar(nextPriod);
    //     }).finally(() => isGlobalLoadingVar(false));
    // };


    /** @description неделя */
    const periodHandler = (periodStart) => {
        const isCurrentPeriod = filterPeriod === periodStart;
        const [firstEEdate] = deliveryDatesEE;
        const firstEEdateISO = firstEEdate && formatDateToISO(firstEEdate.dateObj);

        const dateForSelect = (isCurrentPeriod && firstEEdate) ? firstEEdateISO : periodStart;

        const epochPeriodStart = new Date(dateForSelect).getTime() / 1000;
        const dayDiff = epochPeriodStart - (currentDate / 1000);

        onSelectDate(dayDiff, dateForSelect);
        onSelect(periodStart);
        analyticService.push({
            eventName: 'Change_Filter_Item',
            itemKey: 'period',
            periodIndex: renderPeriodList.findIndex((p) => p.start === periodStart),
        });
    };

    const periodHandlerWithData = (nextPeriod, e) => {
        e.stopPropagation();
        isGlobalLoadingVar(true);
        updateBilling({
            onCompleted: (data) => { onCompletedBillingMutation(data); },
            variables: {
                ...billingVars,
                period: nextPeriod,
                payment_method: billingVars?.payment_method,
            },
        }).then(({ data }) => {
            periodHandler(nextPeriod);
        }).finally(() => isGlobalLoadingVar(false));
    };

    /** @description день  */
    const selectDeliveryDateHandler = (dayDiff) => {
        // TODO:

        analyticService.push({
            eventName: 'Change_Filter_Item',
            itemKey: 'day',
            index: DIFFS.indexOf(dayDiff),
        });

        onSelectDate(dayDiff, filterPeriod);
        onSelect(filterPeriod);
    };

    const selectDeliveryDateHandlerWithData = (event) => {
        event.stopPropagation();
        const diff = event.currentTarget.name;

        if (filterPeriod !== curPeriod) {
            isGlobalLoadingVar(true);
            updateBilling({
                onCompleted: (data) => { onCompletedBillingMutation(data); },
                variables: {
                    ...billingVars,
                    period: filterPeriod,
                },
            }).then(({ data }) => {
                const nextPriod = data?.billing?.cart?.period;
                selectDeliveryDateHandler(diff);
                selectedPeriodVar(nextPriod);
            }).finally(() => isGlobalLoadingVar(false));
        } else {
            selectDeliveryDateHandler(diff);
        }
    };

    const futurePeriods = excludeHiddenPeriods(renderPeriodList)
        .filter((p) => p.start !== filterPeriod && !isDateInRange(filterPeriod, p.start, 1));

    const isMagicPeriodEnabled = false;
    const deliveryDatesForRender = isMagicPeriodEnabled ? deliveryDates : deliveryDatesEE;

    const isDatesExcludedOnPage = isHiddenDatesExcludedOnPage();
    const visibleDeliveryDates = isDatesExcludedOnPage
        ? deliveryDatesForRender
        : deliveryDatesForRender.filter((item) => (
            item.xValue === dateFilterStatus || !isDateHidden(formatDateToISO(item.dateObj))
        ));

    const firstEverydayDate = deliveryDatesEveryday[0];
    const firstEverydayDateISO = firstEverydayDate ? formatDateToISO(deliveryDatesEveryday[0].dateObj) : null;
    const tomorrowISO = tomorrowDeliveryDate ? formatDateToISO(tomorrowDeliveryDate.dateObj) : null;
    const isEverydayMenuAvailableForTomorrow = firstEverydayDateISO === tomorrowISO;

    return (
        <div styleName="period-list" ref={containerRef}>
            {isEverydayMenuAvailableForTomorrow && !isDateHidden(tomorrowISO) && (
                <li styleName="period-list__item" key={tomorrowDeliveryDate.xValue}>
                    <a
                        styleName="period-list__item-button"
                        href={EVERYDAY_SET.href}
                        name={tomorrowDeliveryDate.xValue}
                        data-test-id="tomorrow__desktop"
                    >
                        <Checkbox
                            type={TYPE_RADIO}
                            name={tomorrowDeliveryDate.xValue}
                            id={tomorrowDeliveryDate.xValue}
                            checked={tomorrowDeliveryDate.isChecked}
                            onClick={() => { }}
                            onChange={(e) => e.stopPropagation()}
                        />

                        <div styleName="period-text">
                            <strong>
                                {tomorrowDeliveryDate.day}
                                .
                                &nbsp;
                            </strong>
                            <span styleName="period-day">{tomorrowDeliveryDate.date}</span>
                            &nbsp;
                            {tomorrowDeliveryDate.month}
                        </div>
                    </a>
                </li>
            )}
            {visibleDeliveryDates.map(({
                xValue,
                day,
                date,
                month,
            }) => {
                const isFilterPeriodSelected = filterPeriod === selectedPeriod || isDateInRange(filterPeriod, selectedPeriod, 1);
                const isChecked = (dateFilterStatus === xValue)
                    && isFilterPeriodSelected;

                if (xValue === tomorrowDeliveryDate.xValue) {
                    return null;
                }

                return (
                    <li styleName="period-list__item" key={xValue}>
                        <button
                            styleName="period-list__item-button"
                            onClick={selectDeliveryDateHandlerWithData}
                            type="button"
                            name={xValue}
                            data-test-id="x+day__desktop"
                        >
                            <Checkbox
                                type={TYPE_RADIO}
                                name={xValue}
                                id={xValue}
                                checked={isChecked}
                                onClick={selectDeliveryDateHandlerWithData}
                                onChange={(e) => e.stopPropagation()}
                            />

                            <div styleName="period-text">
                                <strong>
                                    {day}
                                    .
                                    &nbsp;
                                </strong>
                                <span styleName="period-day">{date}</span>
                                &nbsp;
                                {month}
                            </div>
                        </button>
                    </li>
                );
            })}

            {futurePeriods.map((period) => {
                const isSelected = period.start === selectedPeriod;
                const { start, end } = formatPeriodMobile(period, selectedPeriod);

                return (
                    <Fragment key={`${period.start}-${period.end}`}>
                        <li styleName="period-list-item" key={`${period.start}-${period.end}`}>
                            <button
                                type="button"
                                styleName="period-list__item-button"
                                onClick={(e) => {
                                    periodHandlerWithData(period.start, e);
                                }}
                                data-test-id="period__desktop"
                            >
                                <Checkbox
                                    type={TYPE_RADIO}
                                    name="period"
                                    id={`period-${period.start}`}
                                    checked={isSelected}
                                    onClick={(e) => periodHandlerWithData(period.start, e)}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                    }}
                                />
                                <div styleName="period-text">
                                    {period?.title && period.title[locale]}
                                    {!period?.title && (
                                        <>
                                            {' '}
                                            <span styleName="period-day">{start.day}</span>
                                            &nbsp;
                                            {start.month}
                                            &nbsp;-&nbsp;
                                            <span styleName="period-day">{end.day}</span>
                                            &nbsp;
                                            {end.month}
                                        </>
                                    )}
                                </div>
                            </button>
                        </li>
                    </Fragment>
                );
            })}
        </div>
    );
}

export default compose(
    withAvailableDeliveryDates,
)(PeriodList);

PeriodList.propTypes = {
    periods: PropTypes.arrayOf(PropTypes.shape({
        start: PropTypes.string.isRequired,
        end: PropTypes.string.isRequired,
    })).isRequired,
    selectedPeriod: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    isExpanded: PropTypes.bool,
    deliveryDatesEveryday: PropTypes.arrayOf(PropTypes.shape({
        dateObj: PropTypes.shape({}), // new Date()
        xValue: PropTypes.string,
        isChecked: PropTypes.bool,
    })).isRequired,
    tomorrowDeliveryDate: PropTypes.shape({
        dateObj: PropTypes.shape({}), // new Date()
        xValue: PropTypes.string,
        isChecked: PropTypes.bool,
    }),

};
